import * as React from 'react';

import classnames from 'classnames';

import styles from './browser-preview.module.css';

type Props = {
    /** The URL to display in the preview */
    src: string;
    /** Accessible title for the iframe */
    title: string;
    /** Called when the iframe finishes loading */
    onLoad?: () => void;
};

const IFRAME_WIDTH = 1440;
const IFRAME_HEIGHT = 900;

/**
 * A non-interactive browser preview component that displays a website
 * inside a scaled-down browser chrome. Useful for previewing web pages
 * in a read-only context.
 *
 * The component automatically scales the website to fit within its container
 * while maintaining the original desktop viewport size (1440x900).
 */
export function BrowserPreview(props: Props) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [scale, setScale] = React.useState(0.5);

    React.useEffect(() => {
        const updateScale = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                const containerHeight = containerRef.current.offsetHeight;

                const scaleX = containerWidth / IFRAME_WIDTH;
                const scaleY = containerHeight / IFRAME_HEIGHT;
                const newScale = Math.min(scaleX, scaleY);

                setScale(newScale);
            }
        };

        updateScale();

        const resizeObserver = new ResizeObserver(updateScale);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div className={styles['browser-container']}>
            <div className={styles['browser-window']}>
                <div className={styles['browser-header']}>
                    <div className={styles['browser-dots']}>
                        <span className={classnames(styles.dot, styles['dot-red'])} />
                        <span className={classnames(styles.dot, styles['dot-yellow'])} />
                        <span className={classnames(styles.dot, styles['dot-green'])} />
                    </div>
                    <div className={styles['url-bar']}>
                        <span className={styles['url-text']}>{props.src}</span>
                    </div>
                </div>
                <div className={styles['browser-content']} ref={containerRef}>
                    <div
                        className={styles['iframe-scaler']}
                        style={{
                            width: IFRAME_WIDTH,
                            height: IFRAME_HEIGHT * 2,
                            transform: `scale(${scale})`,
                        }}
                    >
                        <iframe
                            className={styles.iframe}
                            src={props.src}
                            title={props.title}
                            onLoad={props.onLoad}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
